import React, { memo, useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@zenith/assets/icons/Close";
import { SelectComponent, TextAreaComponent } from "@zenith/components";
import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import {
  ApiResponseDTOCreateDisputeResponseDTO,
  ApiResponseDTODisputeTypesListResponseDTO,
  CreateDisputeRequestRecord,
  DisputesApiService,
  DisputeTypesListResponseDTO,
} from "@zenith/services/transactions";
import { useTransactionsContext } from "@zenith/context";
import { FormFieldErrorType } from "@zenith/types";
interface DisputeModalComponentProps {
  isOpen: boolean;
  onClose: () => void;
  onDisputeSuccess?: () => void;
  notify: (
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ) => void;
}

const DisputeModalComponent = memo(function DisputeModalComponent(
  props: DisputeModalComponentProps
) {
  const [selectDisputesItems, setSelectDisputesItems] = useState<
    SelectItemsType[]
  >([]);
  const initialErrorState = { hasError: false, message: "" };
  const [selectedDisputesError, setSelectedDisputesError] =
    useState<FormFieldErrorType>(initialErrorState);
  const InitialFormData = {
    errorReportId: "",
    disputeText: "",
    disputeInfo: "",
  };
  const [formData, setFormData] = useState<{
    errorReportId: string;
    disputeText: string;
    disputeInfo: string;
  }>(InitialFormData);

  const { transactionsContext, setTransactionsContext } =
    useTransactionsContext();
  const { transactionId, transactionDate } = transactionsContext;

  const [isLoading, setIsLoading] = useState(false);

  const handleModalClose = () => {
    setSelectedDisputesError(initialErrorState);
    setFormData(InitialFormData);
    props.onClose();
  };

  function onInformationChange(
    event: React.ChangeEvent<HTMLTextAreaElement>
  ): void {
    setFormData({
      ...formData,
      disputeInfo: event.target.value,
    });
  }

  function onDisputeChange(
    event: React.SyntheticEvent<Element, Event> | null,
    newValue: string | null,
    label: string | undefined
  ): void {
    setFormData({
      ...formData,
      errorReportId: newValue as string,
      disputeText: label as string,
    });
  }

  const onDisputeBlur = (newValue: string) => {
    if (!newValue) {
      setSelectedDisputesError({
        hasError: true,
        message: "Error report is required",
      });
      return;
    }
    setSelectedDisputesError({
      hasError: false,
      message: "",
    });
  };

  function onDisputeSubmit() {
    setIsLoading(true);
    const requestBody: CreateDisputeRequestRecord = {
      additionalInformation: formData.disputeInfo,
      disputeType: formData.disputeText,
      transactionId: transactionId as string,
      transactionDate: transactionDate as string,
    };

    DisputesApiService.createDispute(requestBody)
      .then((response: ApiResponseDTOCreateDisputeResponseDTO) => {
        const disputeId = response.data?.disputeId || "N/A";
        props.notify(
          "Dispute submitted successfully",
          `Your request with ID - ${disputeId} has been received and is undergoing review. Feedback will be provided within 48 hours.`,
          "success"
        );
        setTransactionsContext({
          ...transactionsContext,
          disputeId: response.data?.disputeId as string,
        });
        props.onDisputeSuccess?.();
        setIsLoading(false);
        handleModalClose();
      })
      .catch((error) => {
        props.notify(
          "Dispute not submitted",
          error.body?.message || "Unable to create dispute",
          "error"
        );
        setIsLoading(false);
        handleModalClose();
      });
  }

  function isFormValid(): boolean {
    if (!formData.errorReportId || !formData.disputeText) {
      return false;
    }

    return true;
  }

  useEffect(() => {
    if (!selectDisputesItems?.length) {
      DisputesApiService.getTypesList()
        .then((response: ApiResponseDTODisputeTypesListResponseDTO) => {
          if ("data" in response && response.success) {
            const disputes: SelectItemsType[] | undefined = (
              response.data as DisputeTypesListResponseDTO
            ).disputeTypes?.map((dispute) => ({
              value: dispute.id as string,
              label: dispute.text as string,
            }));

            setSelectDisputesItems(disputes as SelectItemsType[]);
          }
        })
        .catch((error) => {
          throw new Error(error);
        });
    }
  }, []);

  return (
    <Modal open={props.isOpen} onClose={handleModalClose}>
      <div className="fixed top-0 left-0 flex h-full w-full items-center justify-center bg-black bg-opacity-50">
        <div className="h-132 w-120 max-w-[94%] rounded-2.5xl bg-white py-6 px-8 shadow">
          <div className="flex flex-col">
            <IconButton
              id="close-dispute-modal"
              onClick={handleModalClose}
              className="self-end p-0 text-neutral-900"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <h1 className="mt-6 mb-16 text-2xl font-normal text-neutral-900">
              Dispute Transaction
            </h1>
          </div>
          <div className="flex h-[77.5%] flex-col justify-between">
            <div className="flex w-full flex-col gap-y-4">
              <SelectComponent
                items={selectDisputesItems}
                listboxZIndex={1300}
                className="w-full"
                label="Error Report"
                id="error-report"
                labelId="error-report-label"
                defaultValue=""
                value={formData.errorReportId}
                placeholder="Select an error report"
                hasError={selectedDisputesError.hasError}
                errorMessage={selectedDisputesError.message}
                onChange={onDisputeChange}
                onClose={onDisputeBlur}
              />
              <TextAreaComponent
                minRows={6}
                id="dispute-textarea"
                labelId="dispute-textarea-label"
                label="Additional information (optional)"
                placeholder="Insert information up to a maximum of 100 characters"
                value={formData.disputeInfo as string}
                onChange={onInformationChange}
                maxLength={100}
              />
            </div>
            <div className="flex flex-row justify-between pt-6">
              <Button
                id="cancel-dispute-button"
                className="h-11 w-73 rounded-lg border-1 border-solid border-neutral-300 text-base font-normal normal-case text-neutral-600 hover:border-neutral-300 hover:bg-white"
                onClick={handleModalClose}
              >
                Cancel
              </Button>
              <Button
                id="submit-dispute-button"
                className="h-11 w-73 rounded-lg bg-primary-300 text-base font-medium normal-case text-white hover:bg-primary-300 disabled:pointer-events-none disabled:bg-neutral-200 disabled:text-neutral-100"
                onClick={onDisputeSubmit}
                disabled={!isFormValid() || isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Submit Dispute"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
});

export default DisputeModalComponent;